import { ArrowRight, Plus, FolderOpen } from "lucide-react";
import { useState, useEffect } from "react";
import PropertyGroupModal, { Data } from "./propertyGroupModal";
import { Search_Domain_Enum, Search_Slugs, Search_Slugs_Set_Input, Session_Origin_Enum, useGetSearchSlugsQuery, useGetSearchSlugByIdLazyQuery, useInsertSearchSlugMutation, usePromptSearchForSearchSlugMutation, useUpdateNaturalLanguageQueryByPkMutation, useUpdateSearchSessionByPkMutation, useUpdateSearchSlugByPkMutation } from "../../api/generated/graphql";
import { Link, CircularProgress, Tooltip } from "@mui/material";
import FolderActions from "./folder-actions";

function SelectedProperties() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<Search_Slugs | null>(null);
  const [insertSearchSlug] = useInsertSearchSlugMutation();
  const [promptSearchForSearchSlug] = usePromptSearchForSearchSlugMutation();
  const [updateSearchSlug] = useUpdateSearchSlugByPkMutation();
  const [updateSearchSession] = useUpdateSearchSessionByPkMutation();
  const [updateNaturalLanguageQuery] = useUpdateNaturalLanguageQueryByPkMutation();
  const { data, refetch, loading } = useGetSearchSlugsQuery();
  const [getSearchSlugById, { data: editDataResponse, loading: editDataLoading, error: editDataError }] = useGetSearchSlugByIdLazyQuery();

  // Update editData when lazy query completes
  useEffect(() => {
    if (editDataResponse?.search_slugs_by_pk) {
      setEditData(editDataResponse.search_slugs_by_pk as Search_Slugs);
    }
  }, [editDataResponse]);

  // Handle edit data fetch errors
  useEffect(() => {
    if (editDataError) {
      console.error('Error fetching edit data:', editDataError);
      // Close modal on error
      setIsModalOpen(false);
      setIsEditMode(false);
      setEditData(null);
    }
  }, [editDataError]);

  const addPropertyGroup = async (propertyGroup: Data) => {
    try {
      // Prepare metadata JSON - pass object directly, GraphQL handles serialization
      // Always include metadata object with keywords array (even if empty)
      const metadata = propertyGroup.metadata
        ? {
            metaTitle: propertyGroup.metadata.meta_title,
            metaDescription: propertyGroup.metadata.meta_description,
            keywords: propertyGroup.metadata.keywords.filter((k) => k.trim() !== ""),
          }
        : {
            metaTitle: "",
            metaDescription: "",
            keywords: [],
          };

      // Prepare style JSON - pass object directly
      const style = propertyGroup.style
        ? {
            properties_section: propertyGroup.style.properties_section,
            features_section: propertyGroup.style.features_section,
          }
        : null;

      // Prepare questions JSON - pass object directly
      const questions = propertyGroup.questions
        ? {
            title: propertyGroup.questions.title,
            description: propertyGroup.questions.description,
            items: propertyGroup.questions.items,
          }
        : null;

      const insertResult = await insertSearchSlug({
        variables: {
          object: {
            description: propertyGroup.mainParagraph || propertyGroup.description,
            slug: propertyGroup.slug,
            image: propertyGroup.imageUrl || propertyGroup.heroImage,
            header_text: propertyGroup.header_text,
            language_id: propertyGroup.language_id,
            metadata: metadata,
            style: style,
            questions: questions,
            sitemap_process: false,
            search_session: {
                data: {
                  title: propertyGroup.title,
                  domain: Search_Domain_Enum.RealEstate,
                  origin: Session_Origin_Enum.SearchEngine
                }
            }
          }
        }
      });

      const sessionId = insertResult.data?.insert_search_slugs_one?.session_id;

      if (!sessionId) {
        throw new Error("Search slug was created, but no session was returned for property generation.");
      }

      const promptSearchResult = await promptSearchForSearchSlug({
        variables: {
          body: {
            session_id: sessionId,
            query: propertyGroup.query,
            is_engine_search: true,
          },
        },
      });

      const generation = promptSearchResult.data?.promptSearch;
      if (!generation?.success) {
        throw new Error(generation?.message || "Search slug was created, but property generation failed.");
      }

      const generatedSessionId = generation.natural_language_query?.session_id;
      if (generatedSessionId && generatedSessionId !== sessionId) {
        await updateSearchSlug({
          variables: {
            slugId: insertResult.data?.insert_search_slugs_one?.id,
            slugSet: {
              session_id: generatedSessionId,
            },
          },
        });
      }

      refetch();
      closeModal();
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const editPropertyGroup = async (dirtyFieldValues: Data) => {
    if (!editData) return;
    const promises: Promise<any>[] = [];
    const mutationNames: string[] = [];

    try {
      // Check if any search_slug fields are dirty (including image, metadata, style, questions)
      const slugSet: Search_Slugs_Set_Input = {};
      
      if (dirtyFieldValues.description !== undefined) {
        slugSet.description = dirtyFieldValues.description;
      }
      if (dirtyFieldValues.mainParagraph !== undefined) {
        slugSet.description = dirtyFieldValues.mainParagraph;
      }
      if (dirtyFieldValues.slug !== undefined) {
        slugSet.slug = dirtyFieldValues.slug;
      }
      if (dirtyFieldValues.language_id !== undefined) {
        slugSet.language_id = dirtyFieldValues.language_id;
      }
      if (dirtyFieldValues.imageUrl !== undefined) {
        slugSet.image = dirtyFieldValues.imageUrl;
      }
      if (dirtyFieldValues.heroImage !== undefined) {
        slugSet.image = dirtyFieldValues.heroImage;
      }
      if (dirtyFieldValues.header_text !== undefined) {
        slugSet.header_text = dirtyFieldValues.header_text;
      }
      if (dirtyFieldValues.metadata !== undefined) {
        slugSet.metadata = {
          metaTitle: dirtyFieldValues.metadata.meta_title,
          metaDescription: dirtyFieldValues.metadata.meta_description,
          keywords: dirtyFieldValues.metadata.keywords.filter((k) => k.trim() !== ""),
        };
      }
      if (dirtyFieldValues.style !== undefined) {
        slugSet.style = {
          properties_section: dirtyFieldValues.style.properties_section,
          features_section: dirtyFieldValues.style.features_section,
        };
      }
      if (dirtyFieldValues.questions !== undefined) {
        slugSet.questions = {
          title: dirtyFieldValues.questions.title,
          description: dirtyFieldValues.questions.description,
          items: dirtyFieldValues.questions.items,
        };
      }
      
      if (Object.keys(slugSet).length > 0) {
        mutationNames.push('search_slug');
        promises.push(
          updateSearchSlug({
            variables: {
              slugId: editData.id,
              slugSet: slugSet
            }
          })
        );
      }

      // Check if search_session fields are dirty
      if (dirtyFieldValues.title !== undefined && editData.search_session?.id) {
        mutationNames.push('search_session');
        promises.push(
          updateSearchSession({
            variables: {
              sessionId: editData.search_session?.id,
              sessionSet: {
                title: dirtyFieldValues.title,
              }
            }
          })
        );
      }

      // Check if natural_language_query fields are dirty
      if (dirtyFieldValues.query !== undefined && editData.search_session?.natural_language_queries?.[0]?.id) {
        mutationNames.push('natural_language_query');
        promises.push(
          updateNaturalLanguageQuery({
            variables: {
              nlqId: editData.search_session?.natural_language_queries?.[0]?.id,
              nlqSet: {
                query: dirtyFieldValues.query,
              }
            }
          })
        );
      }

      // Execute all necessary mutations
      if (promises.length > 0) {
        await Promise.all(promises);
        refetch();
        closeModal();
      } else {
        // No changes detected, just close the modal
        closeModal();
      }
    } catch (error) {
      console.error('Error updating property group:', error);
    }
  };

  const openModal = () => {
    setIsEditMode(false);
    setEditData(null);
    setIsModalOpen(true);
  };

  const openEditModal = (searchSlug: Search_Slugs) => {
    const slugId = searchSlug.id;
    setIsEditMode(true);
    setIsModalOpen(true);
    // Fetch fresh data from database
    getSearchSlugById({
      variables: {
        id: slugId,
        limit: 1, // Minimal limit since we only need the search_slug data
        offset: 0,
      },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditData(null);
  };

  return (
    <div className="flex flex-col w-full h-screen p-2">
      <div className="w-full px-10 pb-5 mx-auto">
        {/* Header */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Handpicked Properties
            </h1>
            <p className="text-gray-600">
            Manage handpicked properties for KaKtu.al landing pages
            </p>
        </div>
        {/* Add Property Group Button */}
        <div className="flex justify-end bg-white rounded-lg shadow-sm border mb-6 p-6">
          <button
            onClick={openModal}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-1 transition-colors duration-200">
            <Plus className="w-5 h-5 mr-2" />
            Add Property Group
          </button>
        </div>
        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <CircularProgress size={60} className="mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Loading Property Groups
              </h3>
              <p className="text-gray-600 text-center max-w-md">
                Please wait while we fetch your handpicked property groups...
              </p>
            </div>
          </div>
        ) : data?.search_slugs && data.search_slugs.length > 0 ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {data.search_slugs.map((searchSlug) => (
               <div key={searchSlug?.id} className="group relative rounded-2xl border border-muted-3 transition-all duration-300 hover:border-[rgba(224,118,67,0.3)] hover:shadow-xl flex flex-col">
                 <Link href={`/property-group/${searchSlug?.id}`} className="block flex-1" style={{ textDecoration: 'none' }}>
                   <div className="relative h-48 overflow-hidden rounded-t-2xl">
                     <img
                       src={searchSlug?.image || ""}
                       alt="handpicked properties"
                       sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                       className="h-full w-full object-cover"
                     />
                   </div>
                   <div className="pt-6 px-6">
                     <h3 className="mb-2 text-xl font-bold text-primary-1">{searchSlug?.header_text}</h3>
                     <Tooltip title={searchSlug?.description}>
                       <p className="mb-3 truncate-2 line-clamp-2 text-sm text-muted-1">{searchSlug?.description}</p>
                     </Tooltip>
                     <div className="flex items-center justify-between">
                       <span className="font-semibold text-primary">
                         {searchSlug?.search_session?.natural_language_queries?.[0]?.queries_property_results_aggregate?.aggregate?.count || 0} {searchSlug?.search_session?.natural_language_queries?.[0]?.queries_property_results_aggregate?.aggregate?.count === 1 ? 'Property' : 'Properties'}
                       </span>
                       <ArrowRight className="h-5 w-5 text-muted-2 transition-all duration-300 group-hover:translate-x-1 group-hover:text-primary" />
                     </div>
                   </div>
                 </Link>
                 <div className="flex justify-end p-2 relative">
                   <FolderActions handleEditProperty={() => openEditModal(searchSlug as Search_Slugs)} />
                 </div>
               </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="bg-gray-50 rounded-full p-8 mb-6">
                <FolderOpen className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Property Groups Yet
              </h3>
              <p className="text-gray-600 text-center mb-8 max-w-md">
                Create your first property group to showcase handpicked properties on your landing pages. 
                Group properties by type, location, or any criteria that makes sense for your business.
              </p>
              <button
                onClick={openModal}
                className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-1 transition-colors duration-200 shadow-sm"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Group
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <PropertyGroupModal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          addPropertyGroup={addPropertyGroup}
          editPropertyGroup={editPropertyGroup}
          editData={editData}
          isEditMode={isEditMode}
          editDataLoading={editDataLoading}
        />
      )}
    </div>
  );
}

export default SelectedProperties;
